import apiClient from '@/lib/api-client'
import type { Client } from '@/shared/types/entities'
import type { ApiResponse } from '@/shared/types/api'
import { useWorkspaceStore } from '@/shared/stores/workspaceStore'

export interface CreateClientDto {
  name: string
}

export interface UpdateClientDto {
  name: string
}

function getWorkspaceId(): string {
  const { selectedWorkspace } = useWorkspaceStore.getState()
  if (!selectedWorkspace) {
    throw new Error('No workspace selected')
  }
  return selectedWorkspace.id
}

interface ClientsApiResponse {
  data: {
    data: {
      clients: Client[]
    }
  }
}

export const clientService = {
  async getClients(includeArchived = false, search?: string): Promise<Client[]> {
    const workspaceId = getWorkspaceId()
    const params: Record<string, string> = { includeArchived: includeArchived.toString() }
    if (search) params.search = search
    const response = await apiClient.get<ClientsApiResponse>(`/workspaces/${workspaceId}/clients`, { params })
    // Handle nested response: response.data.data.data.clients
    const clients = response.data?.data?.data?.clients
    return Array.isArray(clients) ? clients : []
  },

  async createClient(data: CreateClientDto): Promise<Client> {
    const workspaceId = getWorkspaceId()
    const response = await apiClient.post<ApiResponse<Client>>(`/workspaces/${workspaceId}/clients`, data)
    return response.data.data
  },

  async updateClient(id: string, data: UpdateClientDto): Promise<Client> {
    const workspaceId = getWorkspaceId()
    const response = await apiClient.patch<ApiResponse<Client>>(`/workspaces/${workspaceId}/clients/${id}`, data)
    return response.data.data
  },

  async archiveClient(id: string): Promise<void> {
    const workspaceId = getWorkspaceId()
    await apiClient.post(`/workspaces/${workspaceId}/clients/${id}/archive`)
  },

  async restoreClient(id: string): Promise<Client> {
    const workspaceId = getWorkspaceId()
    const response = await apiClient.post<ApiResponse<Client>>(`/workspaces/${workspaceId}/clients/${id}/restore`)
    return response.data.data
  },
}

export default clientService
